/**
 * Reading, bounding and writing the number somebody is typing.
 *
 * Separate from the control so the correctness lives somewhere testable without a DOM, and so the
 * few other places that read a number off the wire can share one answer.
 *
 * Nothing here is locale-aware: the decimal separator is a full stop, thousands separators are not
 * accepted, and neither is exponent notation. That matches the other two implementations of this
 * field (craft-react and craft-widget) and is the right call for a value that goes into a payload.
 */
export type NumberBounds = {
  min?: number | null;
  max?: number | null;
};

/*
 * Anchored at both ends, so this asks whether the whole string is a number rather than how long its
 * numeric prefix is. Every quantifier is optional, which is what lets the partial states a reader
 * passes through on the way to a number — `-`, `.`, `-.` — through as well.
 */
const NUMERIC_TEXT_REG_EXP = /^-?\d*\.?\d*$/;

/**
 * Whether a numeric field may hold this text.
 *
 * **An edit that fails this is refused whole, not trimmed to its numeric part**, and that is the
 * important decision here. Keeping the longest numeric prefix — what this repository used to do —
 * loses data on the most ordinary mistake there is: text `123`, caret after the `1`, a stray `x`,
 * and the field is left holding `1` with the `23` gone and no undo, because a programmatic write to
 * `value` clears the native undo stack. `1` is a perfectly plausible number and it commits without
 * complaint.
 *
 * Stripping the disallowed characters instead is worse again: it fuses digits that were deliberately
 * separated, turning `1.2.3` into `1.23` and `1e5` into `15`. Refusal is the only rule with no
 * silently-wrong-number branch.
 */
export function isNumericText(text: string): boolean {
  return NUMERIC_TEXT_REG_EXP.test(text);
}

/**
 * The number `text` spells, or `null` if it does not spell one.
 *
 * Empty is `null` rather than `0`: a field nobody filled in is not a zero, and `0` is a real answer
 * somebody may mean. The partial states need no special case — `Number('-')`, `Number('.')` and
 * `Number('-.')` are all `NaN`.
 */
export function parseNumericText(text: string): number | null {
  if (text.length === 0) {
    return null;
  }

  const parsed = Number(text);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  // `-0` prints as "0" and compares equal to `0`, so a stored `-0` would be invisible on screen and
  // unreachable through anything but `Object.is`.
  if (parsed === 0) {
    return 0;
  }

  return parsed;
}

/**
 * A number from a field value that may be stringly typed, as custom field values from the API are —
 * the server sends them as strings, and `IdeaCustomFieldValue['value']` is typed accordingly.
 *
 * Anything that is not a finite number becomes `null` — "no value", never `0`. An empty string is
 * the case that matters: `Number('')` is `0`, so reading it with `Number` would turn a field nobody
 * filled in into a real zero.
 */
export function toNumberValue(value: string | number | null | undefined): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  return parseNumericText(value.trim());
}

/**
 * Brings a number inside its bounds.
 *
 * A bound counts only when it is actually a number. The old spelling — `this.min || 0` and
 * `this.max || Number.MAX_SAFE_INTEGER` — is what made a maximum of `0` indistinguishable from "no
 * maximum" and turned an absent minimum into a floor of zero, so no negative number could be
 * entered anywhere.
 *
 * `min` wins where the two contradict: the ceiling is applied first and the floor second. Neither
 * bound can be honoured in that case; this at least picks the same one every time.
 */
export function clampNumber(value: number, bounds: NumberBounds): number {
  let clamped = value;

  if (typeof bounds.max === 'number' && clamped > bounds.max) {
    clamped = bounds.max;
  }

  if (typeof bounds.min === 'number' && clamped < bounds.min) {
    clamped = bounds.min;
  }

  return clamped;
}

/**
 * A finite number written out in full, never in exponent notation.
 *
 * `String` switches to exponent notation below `1e-6` and at or above `1e21`, so `0.0000000001` —
 * ten characters anybody can type into a number field — comes back as `1e-10`. We do not write for
 * mathematicians: no number is ever shown that way. It is also unreadable *back*: `e` is not a
 * character a numeric field accepts, so `1e-10` in a field would be text that the field's own filter
 * refuses, and every later keystroke would be rejected against it.
 *
 * The expansion is textual — the decimal point is moved through the digits `String` already produced
 * — so it is exact. Going through `toFixed` instead would invent digits that were never there
 * (`(0.1).toFixed(20)` is `0.10000000000000000555`), which is the very thing this fix is about.
 *
 * The result always satisfies `isNumericText`, and `Number` reads it back as the same value.
 *
 * No value, and anything not finite, has no text at all rather than an `"undefined"` or a `"NaN"` on
 * screen. That is what makes this usable as *the* way to show a number, which is the point: a second
 * spelling of the same number somewhere else in the app is how `1e-10` gets back in.
 */
export function toPlainNumberText(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '';
  }

  const text = String(value);

  const exponentAt = text.indexOf('e');
  if (exponentAt === -1) {
    return text;
  }

  const exponent = Number(text.slice(exponentAt + 1));
  const mantissa = text.slice(0, exponentAt);
  const sign = mantissa.startsWith('-') ? '-' : '';
  const unsigned = sign.length > 0 ? mantissa.slice(1) : mantissa;

  const pointAt = unsigned.indexOf('.');
  const digits = pointAt === -1 ? unsigned : unsigned.slice(0, pointAt) + unsigned.slice(pointAt + 1);

  // Where the decimal point lands once the exponent is spent moving it.
  const point = (pointAt === -1 ? unsigned.length : pointAt) + exponent;

  if (point <= 0) {
    return `${sign}0.${'0'.repeat(-point)}${digits}`;
  }

  if (point >= digits.length) {
    return `${sign}${digits}${'0'.repeat(point - digits.length)}`;
  }

  return `${sign}${digits.slice(0, point)}.${digits.slice(point)}`;
}
